/**
 * COL-447 — set or clear `profiles.is_demo_account` for one account.
 *
 * A direct table write, deliberately: the flag is PRESENTATION-ONLY and must
 * never appear in an RLS policy, an RPC or an Edge Function (see the guardrail
 * on the column in 20260920100000_profiles_is_demo_account.sql). Routing the
 * write through a SECURITY DEFINER helper or an admin function would be the
 * first step towards the flag meaning "may read more", which is exactly what
 * the guardrail forbids. So it goes through the ordinary client, under the
 * ordinary "Admins can update all profiles" policy, with the column's own
 * trigger (`enforce_profile_is_demo_role`) as the authority on who may set it.
 *
 * That trigger accepts admin and super_admin only — never tech, never the
 * account itself. Callers must gate their control on `canManageTenant`, the
 * client mirror of the same predicate, so the switch cannot appear to someone
 * the database will refuse.
 *
 * Lives outside `admin-account-actions` because everything there is an edge
 * function call; this one is not.
 */
import { supabase } from "@/lib/supabase";

export type DemoFlagResult = {
  ok: boolean;
  /** Human-readable failure reason — already unwrapped from the error body. */
  error?: string;
};

export async function setDemoAccountFlag(
  userId: string,
  isDemoAccount: boolean
): Promise<DemoFlagResult> {
  try {
    const { data, error } = await supabase
      .from("profiles")
      .update({ is_demo_account: isDemoAccount })
      .eq("id", userId)
      .select("id");

    if (error) return { ok: false, error: error.message };
    // RLS filters an UPDATE rather than failing it, so a caller the row-level
    // policy rejects gets zero rows and NO error. Without this check that
    // reads as success and the flag silently never changes.
    if (!data?.length) {
      return {
        ok: false,
        error: "The account was not updated — you may not have permission.",
      };
    }
    return { ok: true };
  } catch (e: unknown) {
    return {
      ok: false,
      error: e instanceof Error ? e.message : "Could not update the demo flag.",
    };
  }
}
